"use client"
import { useSession, signIn } from "next-auth/react";
import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { JourneyEnums } from "@/types/Dashboard";
import { jwtDecode } from "jwt-decode";
import { getValidScope } from "@/utilities/authHelper";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_content", "utm_campaign"] as const;
const OFFLINE_CARD_KEYS = ["quoteReference", "filtertype", "offerId"] as const;

function getParam(queryParams: URLSearchParams, key: string): string {
  return queryParams.get(key) ?? sessionStorage.getItem(`auth.${key}`) ?? '';
}

function buildAuthorizationParams(
  token: string,
  journey: string,
  utms: Record<string, string>,
  offlineCardParams: Record<string, string>,
  additionalScopes: string
) {
  return {
    token,
    ...utms,
    ...(journey === JourneyEnums.OFFLINE_CARD ? offlineCardParams : {}),
    scope: `openid profile offline_access web-dob-api ${additionalScopes}`.trim(),
  };
}

function buildCallbackUrl(
  queryParams: URLSearchParams,
  token: string | null,
  journey: string,
  utms: Record<string, string>,
  offlineCardParams: Record<string, string>
) {
  const baseCallback = queryParams.get("callbackUrl") ?? '/';
  const [callbackPath, callbackQuery] = baseCallback.split('?');
  const preserved = new URLSearchParams(callbackQuery ?? '');

  if (token) preserved.set('token', token);
  for (const [key, value] of Object.entries(utms)) {
    if (value) preserved.set(key, value);
  }
  if (journey === JourneyEnums.OFFLINE_CARD) {
    for (const [key, value] of Object.entries(offlineCardParams)) {
      preserved.set(key, value ?? '');
    }
  }

  return preserved.toString() ? `${callbackPath}?${preserved.toString()}` : callbackPath;
}

function persistAuthState(
  providerId: string,
  isDobAuth: boolean,
  token: string | null,
  journey: string,
  utms: Record<string, string>,
  offlineCardParams: Record<string, string>
) {
  try {
    if (providerId) sessionStorage.setItem('auth.selectedProvider', providerId);
    if (isDobAuth && token) sessionStorage.setItem('auth.token', token);

    for (const [key, value] of Object.entries(utms)) {
      if (value) sessionStorage.setItem(`auth.${key}`, value);
    }
    if (journey === JourneyEnums.OFFLINE_CARD) {
      for (const [key, value] of Object.entries(offlineCardParams)) {
        sessionStorage.setItem(`auth.${key}`, value ?? '');
      }
    }
  } catch { }
}

function handleAuthenticated(
  session: { expires: string; accessToken?: string },
  queryParams: URLSearchParams,
  router: { push: (url: string) => void },
  hasProcessedAuth: { current: boolean }
): boolean {
  if (new Date(session.expires) <= new Date()) {
    window.location.reload();
    return true;
  }
  hasProcessedAuth.current = true;
  router.push(queryParams.get("callbackUrl") || "/");
  return true;
}

function handleUnauthenticated(
  queryParams: URLSearchParams,
  token: string | null,
  hasProcessedAuth: { current: boolean }
) {
  const isDobAuth = !!token;
  const { journey } = token
    ? jwtDecode<{ [key: string]: string }>(token)
    : { journey: JourneyEnums.DASHBOARD };
  const additionalScopes = getValidScope(journey);

  const utms: Record<string, string> = {};
  for (const key of UTM_KEYS) { utms[key] = getParam(queryParams, key); }

  const offlineCardParams: Record<string, string> = {};
  for (const key of OFFLINE_CARD_KEYS) { offlineCardParams[key] = getParam(queryParams, key); }

  const authorizationParams = isDobAuth
    ? buildAuthorizationParams(token as string, journey, utms, offlineCardParams, additionalScopes)
    : undefined;

  const persistedProvider = sessionStorage.getItem('auth.selectedProvider');
  const providerId = (isDobAuth || persistedProvider === 'credability-iterable-dob')
    ? 'credability-iterable-dob'
    : 'credability-auth';

  const callbackUrl = buildCallbackUrl(queryParams, token, journey, utms, offlineCardParams);

  hasProcessedAuth.current = true;
  persistAuthState(providerId, isDobAuth, token, journey, utms, offlineCardParams);

  try {
    signIn(providerId, { redirect: true, callbackUrl }, authorizationParams);
  } catch {
    setTimeout(() => window.location.reload(), 1000);
  }
}

export default function HandleSignin() {
  const router = useRouter();
  const { data: session, status } = useSession();
  const signInInitiated = useRef(false);
  const hasProcessedAuth = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || status === "loading" || hasProcessedAuth.current) {
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get("token") ?? sessionStorage.getItem('auth.token');

    if (status === "authenticated" && session?.accessToken) {
      handleAuthenticated(session, queryParams, router, hasProcessedAuth);
      return;
    }

    if (status === "unauthenticated" && !signInInitiated.current) {
      signInInitiated.current = true;
      handleUnauthenticated(queryParams, token, hasProcessedAuth);
    }
  }, [session, status, router]);

  return <div data-testid="signin"></div>;
}
